import React, { useCallback, useMemo, memo } from 'react';
import {
	INWARD_LINK_DIRECTION,
	OUTWARD_LINK_DIRECTION,
} from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	Dependency,
	Option,
	DependencyActionParams,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/types/edit-dependency-modal.tsx';
import RoadmapDependencyModal from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/edit-dependency-modal/index.tsx';
import { useIssuesMap } from '../../../../common/utils/use-issues-map.tsx';
import { useCreateDependency } from '../../../../services/create-dependency/index.tsx';
import { useRemoveDependency } from '../../../../services/remove-dependency/index.tsx';
import type { EditDependencyModalProps } from './types.tsx';

const EditDependencyModal = ({
	id,
	isOpen,
	triggerRef,
	setCloseModal,
}: EditDependencyModalProps) => {
	const [{ issuesMap }] = useIssuesMap();
	const issue = issuesMap.get(id);
	const { create } = useCreateDependency();
	const { remove } = useRemoveDependency();

	const options = useMemo(() => {
		const candidateIssues: Option[] = [];
		issuesMap.forEach((value, key) => {
			if (key !== id) {
				candidateIssues.push({
					id: key,
					iconUrl: value.issueType.iconUrl,
					issueKey: value.key,
					issueSummary: value.summary || '',
					issueType: value.issueType.name,
				});
			}
		});
		return candidateIssues;
	}, [id, issuesMap]);

	const dependencies = useMemo(() => {
		const linkedDependencies: Dependency[] = [];
		issue?.issueLinks?.forEach((link) => {
			const { inward, outward } = link;
			const issueId = inward ? inward.issueId : outward?.issueId;
			const linkedIssue = issuesMap.get(issueId);
			if (linkedIssue) {
				linkedDependencies.push({
					id: issueId,
					iconUrl: linkedIssue.issueType.iconUrl,
					issueKey: linkedIssue.key,
					issueSummary: linkedIssue.summary || '',
					issueType: linkedIssue.issueType.name,
					link: inward ? INWARD_LINK_DIRECTION : OUTWARD_LINK_DIRECTION,
				});
			}
		});
		return linkedDependencies;
	}, [issue?.issueLinks, issuesMap]);

	const createDependencyPayload = useCallback(
		(dependency: DependencyActionParams['dependency']) =>
			dependency.link === OUTWARD_LINK_DIRECTION
				? {
						inwardIssueId: toIssueId(id),
						outwardIssueId: toIssueId(dependency.id),
					}
				: {
						inwardIssueId: toIssueId(dependency.id),
						outwardIssueId: toIssueId(id),
					},
		[id],
	);

	const onAddDependency = useCallback(
		({ dependency, analyticsEvent }: DependencyActionParams) => {
			fireUIAnalytics(analyticsEvent, 'editDependencyModalAdd');
			create({
				input: createDependencyPayload(dependency),
			});
			return true;
		},
		[create, createDependencyPayload],
	);

	const onUnlinkDependency = useCallback(
		({ dependency, analyticsEvent }: DependencyActionParams) => {
			const unlinkedDependency = issue?.issueLinks?.find((link) => {
				const { inward, outward } = link;
				return dependency.link === OUTWARD_LINK_DIRECTION ? outward?.issueId : inward?.issueId;
			});
			if (unlinkedDependency) {
				fireUIAnalytics(analyticsEvent, 'editDependencyModalUnlink');
				const payload = createDependencyPayload(dependency);
				remove({
					...payload,
					linkId: unlinkedDependency.issueLinkId,
				});
			}
		},
		[createDependencyPayload, issue, remove],
	);

	if (!issue) {
		return null;
	}

	return (
		<RoadmapDependencyModal
			isOpen={isOpen}
			issueType={issue.issueType.name}
			issueKey={issue.key}
			issueSummary={issue.summary || ''}
			iconUrl={issue.issueType.iconUrl}
			dependencies={dependencies}
			options={options}
			shouldReturnFocus={triggerRef}
			setCloseModal={setCloseModal}
			onAddDependency={onAddDependency}
			onUnlinkDependency={onUnlinkDependency}
		/>
	);
};

export default memo(EditDependencyModal);
